// Cookies consent banner - GDPR / Moldova law 133/2011.
// Reopenable at any time from the footer "Setări cookie-uri" link so a visitor
// can review, change or WITHDRAW consent as easily as they gave it.
// Stored shape: { v:1, ts:<iso>, choice:'accept'|'reject'|'custom', categories:{analytics,marketing} }
import { qs, on } from '../lib/dom.js';

const KEY = 'atplCookieConsent';

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); }
  catch { return null; }
}

function write(choice, categories) {
  const payload = JSON.stringify({
    v: 1,
    ts: new Date().toISOString(),
    choice,
    // Granular flags ("Setează preferințele"). Essentials are implicit.
    categories: categories || (choice === 'accept'
      ? { analytics: true, marketing: true }
      : { analytics: false, marketing: false }),
  });
  // Fast client-side path (also read by the tracking gate).
  try { localStorage.setItem(KEY, payload); } catch { }
  // Durable path: a 1-year, first-party cookie set SERVER-side, so the choice
  // survives the script-storage purges (Safari ITP, private mode) that made the
  // banner reappear on every visit. Fire-and-forget; the inline mount script
  // restores it into localStorage on the next load.
  try {
    const AP = (typeof window !== 'undefined' && window.AP_DATA) ? window.AP_DATA : null;
    if (AP && AP.apiBase) {
      fetch(AP.apiBase + 'consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': AP.nonce || '' },
        body: JSON.stringify({ value: payload }),
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => { });
    }
  } catch { }
}

/* ── Withdrawal helpers: when a category is turned OFF, delete the cookies it
      had set (best-effort across host/parent-domain and path). ────────────── */
function cookieNames() {
  return document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter(Boolean);
}
function deleteCookie(name) {
  const host = location.hostname;
  const bare = host.replace(/^www\./, '');
  const parent = '.' + host.split('.').slice(-2).join('.');
  const domains = ['', host, '.' + host, '.' + bare, parent];
  const paths = ['/', location.pathname];
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  paths.forEach((p) => domains.forEach((d) => {
    document.cookie = name + '=; expires=' + past + '; path=' + p + (d ? '; domain=' + d : '') + '; SameSite=Lax';
  }));
}
function clearTrackingCookies(prev, next) {
  const patterns = [];
  if (prev.analytics && !next.analytics) patterns.push(/^_ga($|_)/, /^_gid$/, /^_gat/, /^__utm/);
  if (prev.marketing && !next.marketing) patterns.push(/^_fbp$/, /^_fbc$/, /^_gcl/, /^_uet/, /^fr$/);
  if (!patterns.length) return;
  cookieNames().forEach((n) => { if (patterns.some((re) => re.test(n))) deleteCookie(n); });
}
function isWithdrawal(prev, next) {
  if (!prev) return false;
  return (prev.analytics && !next.analytics) || (prev.marketing && !next.marketing);
}

export function initCookiesBanner() {
  const root = qs('[data-cookie-banner]');
  if (!root) return;

  const prefsBtn = qs('[data-cookie-action="prefs"]', root);
  const saveLabel = (prefsBtn && prefsBtn.dataset.saveLabel) || 'Salvează preferințele';

  // Auto-open on first visit only. Once a decision exists we KEEP the banner in
  // the DOM (hidden) so the footer "cookie settings" link can reopen it - it is
  // no longer removed.
  const existing = read();
  if (!(existing && existing.choice)) {
    requestAnimationFrame(() => {
      root.hidden = false;
      root.classList.add('is-open');
      // The inline mount script suppressed the entrance transition; re-enable it
      // one frame later so the dismissal still animates.
      requestAnimationFrame(() => root.classList.remove('ap-no-anim'));
    });
  }

  function hide() {
    root.classList.remove('is-open');
    root.classList.add('is-out');
    setTimeout(() => { root.classList.remove('is-out'); root.hidden = true; }, 260);
  }

  function decide(choice, categories) {
    // Resolve the category flags the SAME way write() does so the dispatched
    // event always carries real booleans (Accept/Reject pass no categories).
    const cats = categories || (choice === 'accept'
      ? { analytics: true, marketing: true }
      : { analytics: false, marketing: false });
    const prev = read();
    const prevCats = (prev && prev.categories) || null;
    write(choice, cats);
    // Public event so the tracking gate reacts (grants load tags immediately).
    document.dispatchEvent(new CustomEvent('ap:cookie-consent', { detail: { choice, categories: cats } }));

    // Withdrawal (a category went on -> off): the Meta Pixel / call-tracker are
    // already on the page and can't be unloaded, so clear their cookies and
    // reload. On the next load the tracking gate keeps them out (denied).
    if (isWithdrawal(prevCats, cats)) {
      clearTrackingCookies(prevCats, cats);
      root.classList.remove('is-open');
      // Let the fire-and-forget consent POST leave before we navigate.
      setTimeout(() => window.location.reload(), 200);
      return;
    }
    hide();
  }

  // Reopen the banner in preferences mode, toggles pre-filled from the stored
  // choice. Triggered by the footer "Setări cookie-uri" link.
  function openSettings() {
    const cur = read();
    // No prior decision → toggles start UNCHECKED (opt-in, matches the
    // banner markup); a returning visitor sees their stored choice.
    const cats = (cur && cur.categories) || { analytics: false, marketing: false };
    const a = qs('[data-cookie-cat="analytics"]', root);
    const m = qs('[data-cookie-cat="marketing"]', root);
    if (a) a.checked = !!cats.analytics;
    if (m) m.checked = !!cats.marketing;
    const panel = qs('[data-cookie-prefs]', root);
    if (panel) panel.hidden = false;
    if (prefsBtn) {
      prefsBtn.dataset.cookieAction = 'save-prefs';
      prefsBtn.textContent = saveLabel;
    }
    root.hidden = false;
    root.classList.remove('is-out', 'ap-no-anim');
    requestAnimationFrame(() => root.classList.add('is-open'));
  }

  on(root, 'click', (e) => {
    const btn = e.target.closest('[data-cookie-action]');
    if (!btn) return;
    const action = btn.dataset.cookieAction;

    if (action === 'prefs') {
      // First click opens the granular panel and turns this button into the
      // save action; second click saves whatever is ticked.
      const panel = qs('[data-cookie-prefs]', root);
      if (panel && panel.hidden) {
        panel.hidden = false;
        btn.dataset.cookieAction = 'save-prefs';
        btn.textContent = saveLabel;
      }
      return;
    }

    if (action === 'save-prefs') {
      const cats = {
        analytics: !!qs('[data-cookie-cat="analytics"]', root)?.checked,
        marketing: !!qs('[data-cookie-cat="marketing"]', root)?.checked,
      };
      const choice = cats.analytics && cats.marketing
        ? 'accept'
        : (!cats.analytics && !cats.marketing ? 'reject' : 'custom');
      decide(choice, cats);
      return;
    }

    decide(action);
  });

  // Footer "Setări cookie-uri" trigger - it lives OUTSIDE the banner root, so
  // delegate from the document.
  on(document, 'click', (e) => {
    if (e.target.closest('[data-cookie-settings]')) {
      e.preventDefault();
      openSettings();
    }
  });
}
